// src/components/TransactionsByMonth.js
import React, { useState } from 'react'
import { ChevronDown, ChevronUp, Calendar, TrendingUp, TrendingDown } from 'lucide-react'
import TransactionCard from './TransactionCard'

const TransactionsByMonth = ({ transactions, onDelete, showUserInfo = false }) => {
  const [expandedMonths, setExpandedMonths] = useState(new Set())

  // Agrupar transacciones por mes
  const groupTransactionsByMonth = (transactions) => {
    const grouped = {}
    
    transactions.forEach(transaction => {
      const date = new Date(transaction.transaction_date)
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`
      const monthName = date.toLocaleDateString('es-ES', {
        month: 'long',
        year: 'numeric'
      })
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          monthName: monthName.charAt(0).toUpperCase() + monthName.slice(1),
          monthKey,
          transactions: [],
          totalIncome: 0,
          totalExpenses: 0,
          transactionCount: 0
        }
      }
      
      grouped[monthKey].transactions.push(transaction)
      grouped[monthKey].transactionCount++
      
      const amount = parseFloat(transaction.amount)
      if (transaction.type === 'income') {
        grouped[monthKey].totalIncome += amount
      } else {
        grouped[monthKey].totalExpenses += amount
      }
    })
    
    // Convertir a array y ordenar por fecha (más reciente primero)
    return Object.values(grouped).sort((a, b) => {
      return new Date(b.monthKey) - new Date(a.monthKey)
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  const toggleMonth = (monthKey) => {
    const newExpanded = new Set(expandedMonths)
    if (newExpanded.has(monthKey)) {
      newExpanded.delete(monthKey)
    } else {
      newExpanded.add(monthKey)
    }
    setExpandedMonths(newExpanded)
  }

  const monthGroups = groupTransactionsByMonth(transactions)

  // Expandir automáticamente el mes actual
  React.useEffect(() => {
    if (monthGroups.length > 0) {
      const currentMonth = monthGroups[0].monthKey
      setExpandedMonths(new Set([currentMonth]))
    }
  }, [transactions])

  if (transactions.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No hay transacciones para mostrar</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {monthGroups.map((monthGroup) => {
        const isExpanded = expandedMonths.has(monthGroup.monthKey)
        const netAmount = monthGroup.totalIncome - monthGroup.totalExpenses
        
        return (
          <div key={monthGroup.monthKey} className="bg-white rounded-lg shadow">
            {/* Header del mes */}
            <div 
              className="px-6 py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleMonth(monthGroup.monthKey)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900">
                      {monthGroup.monthName}
                    </h3>
                  </div>
                  
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {monthGroup.transactionCount} movimientos
                  </span>
                </div>
                
                <div className="flex items-center space-x-6">
                  {/* Ingresos del mes */}
                  <div className="flex items-center space-x-1 text-green-600">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {formatCurrency(monthGroup.totalIncome)}
                    </span>
                  </div>
                  
                  {/* Gastos del mes */}
                  <div className="flex items-center space-x-1 text-red-600">
                    <TrendingDown className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {formatCurrency(monthGroup.totalExpenses)}
                    </span>
                  </div>
                  
                  {/* Balance neto del mes */}
                  <div className={`text-sm font-semibold ${
                    netAmount >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {netAmount >= 0 ? '+' : ''}{formatCurrency(netAmount)}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Transacciones del mes */}
            {isExpanded && (
              <div className="divide-y divide-gray-200">
                {monthGroup.transactions
                  .sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date))
                  .map((transaction) => (
                    <TransactionCard
                      key={transaction.id}
                      transaction={transaction}
                      onDelete={onDelete}
                      showUserInfo={showUserInfo}
                      showMonth={false} // No mostrar mes individual ya que está agrupado
                    />
                  ))
                }
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default TransactionsByMonth